import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table';
import {
    Card,
    Tag,
    Space,
    Button,
    Typography,
    Input,
    Modal,
    Descriptions,
    message,
    Row,
    Col,
    Statistic,
    Dropdown,
    Checkbox,
    TimePicker,
    Empty,
    Tooltip,
    Select,
    DatePicker
} from 'antd';
import {
    EyeOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CalendarOutlined,
    FilterOutlined,
    LeftOutlined,
    RightOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined
} from '@ant-design/icons';
import { bookingsDB, timeSlotsDB } from '../../services/firebase/database';
import moment from 'moment';
import './BookingsManagement.css';

const { Text } = Typography;
const { Search } = Input;

const BookingsManagement = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);

    // Table State
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState([]);
    const [sortField, setSortField] = useState('updatedAt'); // Default: Last Updated based on user request
    const [sortDirection, setSortDirection] = useState('desc'); // Default: Latest first

    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        confirmed: 0,
        completed: 0,
        cancelled: 0
    });

    const loadBookings = useCallback(async () => {
        setLoading(true);
        try {
            const allBookings = await bookingsDB.getAll();

            // Filter to show only payment-approved bookings and confirmed bookings
            const relevantBookings = allBookings.filter(booking =>
                booking.status === 'payment_approved' ||
                booking.status === 'confirmed' ||
                booking.status === 'completed' ||
                booking.status === 'cancelled'
            );

            setBookings(relevantBookings);
            calculateStats(relevantBookings);
        } catch (error) {
            console.error('Error loading bookings:', error);
            message.error('Failed to load bookings');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadBookings();
    }, [loadBookings]);

    // Sort Bookings Data
    const sortedBookings = useMemo(() => {
        const sorted = [...bookings];
        sorted.sort((a, b) => {
            const getTimestamp = (obj, field) => {
                const val = obj[field];
                if (!val) return 0;
                // Handle Firestore Timestamp or JS Date
                return val.toDate ? val.toDate().getTime() : new Date(val).getTime();
            };

            const valA = getTimestamp(a, sortField);
            const valB = getTimestamp(b, sortField);

            if (sortDirection === 'asc') {
                return valA - valB; // Oldest first
            } else {
                return valB - valA; // Newest first
            }
        });
        return sorted;
    }, [bookings, sortField, sortDirection]);

    const toggleSortDirection = () => {
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    const calculateStats = (bookingsList) => {
        const stats = {
            total: bookingsList.length,
            pending: bookingsList.filter(b => b.status === 'payment_approved').length,
            confirmed: bookingsList.filter(b => b.status === 'confirmed').length,
            completed: bookingsList.filter(b => b.status === 'completed').length,
            cancelled: bookingsList.filter(b => b.status === 'cancelled' || b.status === 'payment_rejected').length
        };
        setStats(stats);
    };

    const handleStatusChange = async (bookingId, newStatus) => {
        try {
            const booking = bookings.find(b => b.id === bookingId);
            await bookingsDB.updateStatus(bookingId, newStatus);

            if (booking?.preferredDate && booking?.preferredTime) {
                const dateStr = moment(booking.preferredDate.toDate ? booking.preferredDate.toDate() : booking.preferredDate).format('YYYY-MM-DD');
                const timeStr = moment(booking.preferredTime.toDate ? booking.preferredTime.toDate() : booking.preferredTime).format('HH:mm');

                if (newStatus === 'confirmed') {
                    await timeSlotsDB.bookSlot(dateStr, timeStr, {
                        bookingId: booking.id,
                        patientName: `${booking.firstName} ${booking.lastName}`,
                        patientEmail: booking.email,
                        serviceType: booking.selectedServices?.[0]?.title || 'Consultation',
                        notes: `Booking confirmed: ${booking.confirmationNumber}`
                    });
                } else if (newStatus === 'cancelled' || newStatus === 'completed') {
                    await timeSlotsDB.unblockSlot(dateStr, timeStr, { adminOverride: true });
                }
            }

            message.success(`Booking ${newStatus} successfully`);
            loadBookings();
        } catch (error) {
            console.error('Error updating booking status:', error);
            message.error('Failed to update booking status');
        }
    };

    const handleConfirmBooking = async (booking) => {
        try {
            setLoading(true);
            const adminData = {
                adminId: 'admin_001',
                notes: `Booking confirmed via admin interface for ${booking.confirmationNumber}`
            };
            const result = await timeSlotsDB.atomicAdminConfirmBooking(booking.id, adminData);
            console.log('Booking confirmed:', result);
            message.success('Booking confirmed successfully! Meet link has been sent to the patient.');
            loadBookings();
        } catch (error) {
            console.error('Error confirming booking:', error);
            message.error('Failed to confirm booking: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRejectBooking = async (booking) => {
        const reason = prompt('Please provide a reason for rejection:');
        if (!reason) return;

        try {
            setLoading(true);
            const rejectionData = {
                adminId: 'admin_001',
                reason: reason,
                notes: `Booking rejected via admin interface for ${booking.confirmationNumber}`
            };
            await timeSlotsDB.atomicAdminRejectBooking(booking.id, rejectionData);
            message.success('Booking rejected successfully. Patient has been notified.');
            loadBookings();
        } catch (error) {
            console.error('Error rejecting booking:', error);
            message.error('Failed to reject booking: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const showBookingDetails = (booking) => {
        setSelectedBooking(booking);
        setDetailModalVisible(true);
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            payment_approved: 'gold',
            confirmed: 'blue',
            completed: 'green',
            cancelled: 'red',
            payment_rejected: 'red'
        };
        return colors[status] || 'default';
    };

    // Derive unique Services for Filter Options
    const serviceOptions = useMemo(() => {
        const services = new Set();
        bookings.forEach(b => {
            b.selectedServices?.forEach(s => services.add(s.title));
        });
        return Array.from(services).map(s => ({ label: s, value: s }));
    }, [bookings]);


    // Columns Definition
    const columns = useMemo(() => [
        {
            accessorKey: 'confirmationNumber',
            header: 'Conf #',
            width: 100,
            cell: info => <Text code>{info.getValue()}</Text>
        },
        {
            id: 'patient',
            header: ({ column }) => (
                <div className="column-header-content">
                    <span>Patient</span>
                    <Tooltip title={sortDirection === 'asc' ? "Oldest to Newest" : "Newest to Oldest"}>
                        <Button
                            type="text"
                            size="small"
                            icon={sortDirection === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
                            onClick={toggleSortDirection}
                            style={{ marginLeft: 8 }}
                        />
                    </Tooltip>
                </div>
            ),
            accessorFn: row => `${row.firstName} ${row.lastName}`,
            cell: ({ row }) => (
                <div>
                    <Text strong>{row.original.firstName} {row.original.lastName}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {row.original.email}
                    </Text>
                </div>
            )
        },
        {
            id: 'services',
            accessorFn: row => row.selectedServices?.map(s => s.title).join(', '),
            header: ({ column }) => {
                const isFiltered = column.getIsFiltered();
                const currentFilter = (column.getFilterValue() || []);

                return (
                    <div className="column-header-content">
                        <span>Services</span>
                        <Dropdown
                            trigger={['click']}
                            dropdownRender={() => (
                                <div className="filter-dropdown-content">
                                    <div className="filter-checkbox-group">
                                        <Checkbox.Group
                                            options={serviceOptions}
                                            value={currentFilter}
                                            onChange={(checkedValues) => {
                                                column.setFilterValue(checkedValues.length ? checkedValues : undefined);
                                            }}
                                            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                                        />
                                        {serviceOptions.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Services" />}
                                    </div>
                                    <div style={{ borderTop: '1px solid #eee', paddingTop: '8px', marginTop: '8px', textAlign: 'right' }}>
                                        <Button
                                            size="small"
                                            type="link"
                                            onClick={() => column.setFilterValue(undefined)}
                                            disabled={!isFiltered}
                                        >
                                            Clear
                                        </Button>
                                    </div>
                                </div>
                            )}
                        >
                            <Button
                                className={`header-filter-btn \${isFiltered ? 'active' : ''}`}
                                icon={<FilterOutlined />}
                                size="small"
                            />
                        </Dropdown>
                    </div>
                );
            },
            cell: ({ row }) => (
                <div>
                    {row.original.selectedServices?.slice(0, 2).map((service, index) => (
                        <Tag key={index} style={{ marginBottom: '4px' }}>
                            {service.title}
                        </Tag>
                    ))}
                    {row.original.selectedServices?.length > 2 && (
                        <Tag>+{row.original.selectedServices.length - 2} more</Tag>
                    )}
                </div>
            ),
            filterFn: (row, id, filterValue) => {
                if (!filterValue || filterValue.length === 0) return true;
                const rowServices = row.original.selectedServices?.map(s => s.title) || [];
                // Check if row has ANY of the selected filter services
                return rowServices.some(s => filterValue.includes(s));
            }
        },
        {
            accessorKey: 'preferredDate',
            header: ({ column }) => {
                const isFiltered = column.getIsFiltered();
                const currentFilter = column.getFilterValue();

                return (
                    <div className="column-header-content">
                        <span>Date</span>
                        <Dropdown
                            trigger={['click']}
                            dropdownRender={() => (
                                <div className="filter-dropdown-content" style={{ padding: '12px' }}>
                                    <div style={{ marginBottom: '8px' }}>Select Date Range:</div>
                                    <DatePicker.RangePicker
                                        onChange={(dates) => {
                                            column.setFilterValue(dates);
                                        }}
                                        value={currentFilter}
                                        style={{ width: '100%' }}
                                    />
                                    <div style={{ marginTop: '8px', textAlign: 'right' }}>
                                        <Button
                                            size="small"
                                            type="link"
                                            onClick={() => column.setFilterValue(undefined)}
                                            disabled={!isFiltered}
                                        >
                                            Clear
                                        </Button>
                                    </div>
                                </div>
                            )}
                        >
                            <Button
                                className={`header-filter-btn \${isFiltered ? 'active' : ''}`}
                                icon={<FilterOutlined />}
                                size="small"
                            />
                        </Dropdown>
                    </div>
                );
            },
            cell: info => {
                const date = info.getValue();
                if (!date) return 'Not set';
                return moment(date.toDate ? date.toDate() : date).format('MMM DD, YYYY');
            },
            filterFn: (row, id, filterValue) => {
                if (!filterValue || filterValue.length !== 2) return true;
                const [start, end] = filterValue;
                if (!start || !end) return true;

                const rowVal = row.original.preferredDate;
                if (!rowVal) return false;

                // Ensure robust date handling
                const rDate = moment(rowVal.toDate ? rowVal.toDate() : rowVal);
                const sDate = moment(start.toDate ? start.toDate() : start).startOf('day');
                const eDate = moment(end.toDate ? end.toDate() : end).endOf('day');

                return rDate.isBetween(sDate, eDate, null, '[]');
            }
        },
        {
            id: 'preferredTime',
            accessorFn: row => {
                const t = row.preferredTime;
                return t ? moment(t.toDate ? t.toDate() : t).format('HH:mm') : '';
            },
            header: ({ column }) => {
                const isFiltered = column.getIsFiltered();

                return (
                    <div className="column-header-content">
                        <span>Preferred Time</span>
                        <Dropdown
                            trigger={['click']}
                            dropdownRender={() => (
                                <div className="filter-dropdown-content" style={{ padding: '12px' }}>
                                    <div style={{ marginBottom: '8px' }}>Select Time Range:</div>
                                    <TimePicker.RangePicker
                                        format="HH:mm"
                                        onChange={(dates) => {
                                            if (dates) {
                                                // Store as [startTimeString, endTimeString]
                                                column.setFilterValue([dates[0].format('HH:mm'), dates[1].format('HH:mm')]);
                                            } else {
                                                column.setFilterValue(undefined);
                                            }
                                        }}
                                    />
                                    <div style={{ marginTop: '8px', textAlign: 'right' }}>
                                        <Button
                                            size="small"
                                            type="link"
                                            onClick={() => {
                                                column.setFilterValue(undefined);
                                            }}
                                            disabled={!isFiltered}
                                        >
                                            Clear
                                        </Button>
                                    </div>
                                </div>
                            )}
                        >
                            <Button
                                className={`header-filter-btn \${isFiltered ? 'active' : ''}`}
                                icon={<FilterOutlined />}
                                size="small"
                            />
                        </Dropdown>
                    </div>
                )
            },
            cell: ({ row }) => {
                const time = row.original.preferredTime;
                if (!time) return 'Not set';
                return moment(time.toDate ? time.toDate() : time).format('HH:mm A');
            },
            filterFn: (row, id, filterValue) => {
                // filterValue is [startStr, endStr] e.g. ["09:00", "12:00"]
                if (!filterValue) return true;

                const rowTimeVal = row.original.preferredTime;
                if (!rowTimeVal) return false;

                const rowMoment = moment(rowTimeVal.toDate ? rowTimeVal.toDate() : rowTimeVal);
                const rowTimeStr = rowMoment.format('HH:mm'); // "14:30"

                // Compare strings or moments. String comparison works for HH:mm 24h format.
                return rowTimeStr >= filterValue[0] && rowTimeStr <= filterValue[1];
            }
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: info => (
                <Tag color={getStatusColor(info.getValue())}>
                    {info.getValue()?.toUpperCase()}
                </Tag>
            )
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <Space size="small">
                    <Tooltip title="View Details">
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => showBookingDetails(row.original)}
                        />
                    </Tooltip>

                    {row.original.status === 'payment_approved' && (
                        <>
                            <Tooltip title="Approve">
                                <Button
                                    size="small"
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => handleConfirmBooking(row.original)}
                                    loading={loading}
                                />
                            </Tooltip>
                            <Tooltip title="Reject">
                                <Button
                                    size="small"
                                    danger
                                    icon={<CloseCircleOutlined />}
                                    onClick={() => handleRejectBooking(row.original)}
                                    loading={loading}
                                />
                            </Tooltip>
                        </>
                    )}

                    {row.original.status === 'confirmed' && row.original.meetLink && (
                        <Tooltip title="Join Meeting">
                            <Button
                                size="small"
                                type="link"
                                onClick={() => window.open(row.original.meetLink.url || row.original.meetLink, '_blank')}
                                style={{ padding: 0 }}
                            >
                                🎥
                            </Button>
                        </Tooltip>
                    )}

                    {(row.original.status === 'confirmed' || row.original.status === 'pending') && (
                        <Tooltip title="Cancel Booking">
                            <Button
                                size="small"
                                danger
                                icon={<CloseCircleOutlined />}
                                onClick={() => {
                                    if (window.confirm('Are you sure you want to cancel this booking?')) {
                                        handleStatusChange(row.original.id, 'cancelled');
                                    }
                                }}
                                loading={loading}
                            />
                        </Tooltip>
                    )}
                </Space>
            )
        }
    ], [serviceOptions, loading]);


    const table = useReactTable({
        data: sortedBookings, // Use sorted data
        columns,
        state: {
            globalFilter,
            columnFilters
        },
        onGlobalFilterChange: setGlobalFilter,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            }
        }
    });

    return (
        <div className="bookings-management-container">
            {/* Statistics */}
            <Row gutter={16} className="bookings-stats-row">
                <Col span={4}>
                    <Card className="bookings-stat-card">
                        <Statistic
                            title="Total Bookings"
                            value={stats.total}
                            prefix={<CalendarOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={5}>
                    <Card className="bookings-stat-card">
                        <Statistic
                            title="Pending"
                            value={stats.pending}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col span={5}>
                    <Card className="bookings-stat-card">
                        <Statistic
                            title="Confirmed"
                            value={stats.confirmed}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={5}>
                    <Card className="bookings-stat-card">
                        <Statistic
                            title="Completed"
                            value={stats.completed}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={5}>
                    <Card className="bookings-stat-card">
                        <Statistic
                            title="Cancelled"
                            value={stats.cancelled}
                            valueStyle={{ color: '#f5222d' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card className="bookings-filters-card">
                <Row gutter={16} align="middle">
                    <Col span={8}>
                        <Search
                            placeholder="Search by name, email, conf #..."
                            value={globalFilter ?? ''}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col span={8} style={{ textAlign: 'center' }}>
                        {/* Spacer or additional filters if needed */}
                    </Col>
                    <Col span={8} style={{ textAlign: 'right' }}>
                        <Button onClick={loadBookings} icon={<FilterOutlined />}>Refresh Data</Button>
                    </Col>
                </Row>
            </Card>

            {/* TanStack Table Rendering */}
            <Card className="bookings-table-card" bodyStyle={{ padding: 0 }}>
                <div className="tanstack-table-container">
                    <table className="tanstack-table">
                        <thead>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map(header => (
                                        <th key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={columns.length} style={{ textAlign: 'center', padding: '40px' }}>Loading...</td></tr>
                            ) : table.getRowModel().rows.length === 0 ? (
                                <tr><td colSpan={columns.length} style={{ textAlign: 'center', padding: '40px' }}>No bookings found</td></tr>
                            ) : (
                                table.getRowModel().rows.map(row => (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="pagination-container" style={{ padding: '16px' }}>
                    <span className="page-info">
                        Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                    </span>

                    <Select
                        value={table.getState().pagination.pageSize}
                        onChange={e => {
                            table.setPageSize(Number(e));
                        }}
                        className="page-size-select"
                        options={[
                            { label: '10 / page', value: 10 },
                            { label: '20 / page', value: 20 },
                            { label: '50 / page', value: 50 },
                        ]}
                    />

                    <div className="pagination-buttons">
                        <Button
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                            icon={<LeftOutlined />}
                        />
                        <Button
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                            icon={<RightOutlined />}
                        />
                    </div>
                </div>
            </Card>


            {/* Booking Details Modal */}
            <Modal
                title={`Booking Details - ${selectedBooking?.confirmationNumber}`}
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={null}
                width={800}
            >
                {selectedBooking && (
                    <div>
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label="Confirmation #" span={2}>
                                <Text code>{selectedBooking.confirmationNumber}</Text>
                                <Tag color={getStatusColor(selectedBooking.status)} style={{ marginLeft: 8 }}>
                                    {selectedBooking.status?.toUpperCase()}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Name">
                                {selectedBooking.firstName} {selectedBooking.lastName}
                            </Descriptions.Item>
                            <Descriptions.Item label="Email">
                                <a href={`mailto:${selectedBooking.email}`}>{selectedBooking.email}</a>
                            </Descriptions.Item>
                            <Descriptions.Item label="Phone">
                                {selectedBooking.phone}
                            </Descriptions.Item>
                            <Descriptions.Item label="Date">
                                {selectedBooking.preferredDate ? moment(selectedBooking.preferredDate.toDate ? selectedBooking.preferredDate.toDate() : selectedBooking.preferredDate).format('YYYY-MM-DD') : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Time">
                                {selectedBooking.preferredTime ? moment(selectedBooking.preferredTime.toDate ? selectedBooking.preferredTime.toDate() : selectedBooking.preferredTime).format('HH:mm') : '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <div style={{ marginTop: 24, textAlign: 'right' }}>
                            <Button onClick={() => setDetailModalVisible(false)}>
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default BookingsManagement;
